import React                        from 'react'
import { Box,
         TextField,
         IconButton,
         Typography,
         Button,
         Tooltip,
         useTheme,
       }                            from '@mui/material'
import AddIcon                      from '@mui/icons-material/Add'
import { AnswerChoice, SubChoice } from 'types/apis/surveyInterfaces'
import { SubChoicesSection }        from './SubChoicesSection'
import { Trash2Icon, Info }        from 'lucide-react'
import { ChoiceReadIntentEditor }  from './mapperBuilder/ChoiceReadIntentEditor'
import { deriveChoiceValue }       from './mapperBuilder/deriveChoiceValue'
import { MapperScopeConstraint }   from './mapperBuilder/cascadeTypes'
import { MapperCatalog }           from 'types/apis/surveyInterfaces'

export interface AnswerChoicesSectionProps {
  choices: AnswerChoice[]
  onChoicesChange: (choices: AnswerChoice[]) => void
  questionType: string
  isReadOnly?: boolean
  errors?: any
  // DA-2184: HP/AP choices author their READ intent inline (ChoiceReadIntentEditor); the BE
  // derives write/unselect/stateMapper on save. METADATA never reaches this component
  // (SM-18 hides choices for it).
  profileCode?: string
  // DA-2184: question-level TARGET scope (attribute column / appliance id) that pins each
  // choice's read-condition target. Operator + value stay free per choice.
  mapperScope?: MapperScopeConstraint
  // DA-2184: the account-type-filtered mapper catalog, passed down so the inline read-intent
  // editors use the same filtered attribute set as the rest of the form (no extra fetch).
  catalog?: MapperCatalog
  catalogLoading?: boolean
  // DA-2301: catalog fetch failure, forwarded so each choice's read-intent editor surfaces it
  // instead of silently showing empty pickers.
  catalogError?: string
  isChoiceFieldModified?: (choiceIndex: number, fieldName: keyof AnswerChoice) => boolean
  // SM-25: rolls up the structured mapper fields — for AUTO choices the raw mapper
  // strings are BE-derived, so only these change locally when the read intent is edited.
  isChoiceMapperModified?: (choiceIndex: number) => boolean
  isSubChoiceFieldModified?: (choiceIndex: number, subChoiceIndex: number, fieldName: keyof SubChoice) => boolean
  getModifiedChoiceFieldStyle?: (isModified: boolean) => any
  getModifiedChoiceSelectStyle?: (isModified: boolean) => any
  getModifiedBuilderStyle?: (isModified: boolean) => any
}

export const AnswerChoicesSection: React.FC<AnswerChoicesSectionProps> = ({
  choices,
  onChoicesChange,
  questionType,
  isReadOnly = false,
  errors,
  profileCode,
  mapperScope,
  catalog,
  catalogLoading,
  catalogError,
  isChoiceFieldModified,
  isChoiceMapperModified,
  isSubChoiceFieldModified,
  getModifiedChoiceFieldStyle,
  getModifiedChoiceSelectStyle,
  getModifiedBuilderStyle,
}) => {

  const showSubChoices = questionType === 'MULTIPLE_WITH_SUB_CHOICES'
  const theme = useTheme()
  // DA-2184: HP/AP choices author READ intent inline; METADATA/None never do.
  const showMapperBuilder = (profileCode === 'HP' || profileCode === 'AP') && !isReadOnly
  // DA-2184: read/write/unselect are BE-derived — surfaced on demand via a per-choice info
  // hover (the old always-on 3-column block is gone), for HP/AP whether or not editing.
  const showMapperInfo = profileCode === 'HP' || profileCode === 'AP'

  // DA-2184: the derived read/write/unselect strings, shown read-only inside the info hover
  // (the single place they surface now). Monospace, wrapped so they stay legible.
  const renderMapperInfo = (choice: AnswerChoice) => {
    const rows: Array<[string, string | undefined]> = [
      ['Read', choice.readMapper],
      ['Write', choice.writeMapper],
      ['Unselect', choice.unselectMapper],
    ]
    return (
      <Box sx={{ p: 0.5 }}>
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ minWidth: 64, flexShrink: 0, fontWeight: 700, color: 'text.primary' }}>
              {label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: value ? 'text.primary' : 'text.disabled' }}
            >
              {value || '—'}
            </Typography>
          </Box>
        ))}
        <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          Derived on save
        </Typography>
      </Box>
    )
  }

  const handleAddChoice = () => {
    const newChoice: AnswerChoice = {
      title: '',
      value: '',
      readMapper: '',
      writeMapper: '',
      unselectMapper: '',
      order: choices.length + 1,
      ...(showSubChoices ? { subChoices: [] } : {}),
    } as AnswerChoice
    onChoicesChange([...choices, newChoice])
  }

  const handleDeleteChoice = (index: number) => {
    const updated = choices.filter((_, i) => i !== index)
    // Reorder remaining choices
    const reordered = updated.map((choice, i) => ({ ...choice, order: i + 1 }))
    onChoicesChange(reordered)
  }

  const handleChoiceChange = (index: number, field: keyof AnswerChoice, value: any) => {
    const updated = [...choices]
    updated[index] = { ...updated[index], [field]: value }
    // DA-2184: `value` is no longer authored — derive it from the Display Text (same base slug
    // as the BE) so the choice keeps a stable identity/localization key as the title changes.
    // De-dup is the BE's job; we never do it here.
    if (field === 'title') {
      updated[index].value = deriveChoiceValue(value)
    }
    onChoicesChange(updated)
  }

  const handleSubChoicesChange = (index: number, subChoices: any[]) => {
    const updated = [...choices]
    updated[index] = { ...updated[index], subChoices }
    onChoicesChange(updated)
  }

  // DA-2184: the inline READ-intent editor hands back the whole choice (structured READ
  // clauses + mapperSource) in one shot — write-back goes through the same onChoicesChange
  // path every other choice edit uses, so diff + save stay untouched.
  const handleReadIntentChange = (index: number, updatedChoice: AnswerChoice) => {
    const updated = [...choices]
    updated[index] = updatedChoice
    onChoicesChange(updated)
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
        Answer Choices
      </Typography>
      {choices.map((choice, index) => (
        <Box
          key={index}
          sx={{
            backgroundColor: theme.palette.background.default,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.25,
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {/* Compact header: the Display Text label lives on the field itself; this row only
                carries the info hover + delete, right-aligned. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 24 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {showMapperInfo && (
                  <Tooltip
                    arrow
                    title={renderMapperInfo(choice)}
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 480,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: 4,
                          p: 1,
                        },
                      },
                      arrow: { sx: { color: 'background.paper' } },
                    }}
                  >
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      aria-label="Derived read, write and unselect mappers"
                      // tabIndex + role make the span focusable so the Tooltip opens on keyboard
                      // focus (a hover-only span is unreachable for keyboard/SR users).
                      sx={{ display: 'inline-flex', color: 'text.secondary', cursor: 'help' }}
                    >
                      <Info size={16} />
                    </Box>
                  </Tooltip>
                )}
                {choices.length > 0 && !isReadOnly && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteChoice(index)}
                    aria-label={`Delete choice ${index + 1}`}
                    sx={{ color: 'error.main' }}
                  >
                    <Trash2Icon size={18} />
                  </IconButton>
                )}
              </Box>
            </Box>
            <TextField
              label="Display Text"
              value={choice.title}
              onChange={(e) => handleChoiceChange(index, 'title', e.target.value)}
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: theme.palette.background.paper,
                },
                ...(getModifiedChoiceFieldStyle && isChoiceFieldModified
                  ? getModifiedChoiceFieldStyle(isChoiceFieldModified(index, 'title'))
                  : {}),
              }}
              required
              disabled={isReadOnly}
              error={!!errors?.choices?.[index]?.title}
              helperText={errors?.choices?.[index]?.title?.message || 'Max 50 characters'}
              inputProps={{ maxLength: 50 }}
            />
            {showMapperBuilder && (
              <Box
                sx={getModifiedBuilderStyle && isChoiceMapperModified
                  ? getModifiedBuilderStyle(isChoiceMapperModified(index))
                  : {}}
              >
                <ChoiceReadIntentEditor
                  choice={choice}
                  profileCode={profileCode as 'HP' | 'AP'}
                  questionType={questionType}
                  scope={mapperScope}
                  catalog={catalog}
                  loading={catalogLoading}
                  error={catalogError}
                  disabled={isReadOnly}
                  onChange={(updatedChoice) => handleReadIntentChange(index, updatedChoice)}
                />
              </Box>
            )}
            {showSubChoices && (
              <SubChoicesSection
                subChoices={choice.subChoices || []}
                onSubChoicesChange={(subChoices) => handleSubChoicesChange(index, subChoices)}
                isReadOnly={isReadOnly}
                parentChoiceIndex={index}
                isSubChoiceFieldModified={isSubChoiceFieldModified}
                getModifiedChoiceFieldStyle={getModifiedChoiceFieldStyle}
                getModifiedChoiceSelectStyle={getModifiedChoiceSelectStyle}
              />
            )}
          </Box>
        </Box>
      ))}
      {!isReadOnly && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddChoice}
          variant="outlined"
          size="small"
        >
          Add Choice
        </Button>
      )}
    </Box>
  )
}

